import { ModelFormatItem } from '@shiftly/shared/src/schema';
import { v4 as uuidv4 } from 'uuid';

export class FormatDecoder {
  private static parseTimeString(timeStr: string): string {
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    // Validate time
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      throw new Error(`Invalid time format: ${timeStr}`);
    }
    
    // Convert to 24-hour format, keeping the original hour value since it's already in 12-hour format
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  private static parseFormatItemContent(content: string): Omit<ModelFormatItem, 'id' | 'time'> {
    const trimmedContent = content.trim();

    // Check for conditional format (contains " / ")
    if (trimmedContent.includes(' / ')) {
      const [amPart, pmPart] = trimmedContent.split(' / ').map(s => s.trim());
      
      // Clean up the AM/PM parts - remove any AM/PM suffixes
      const cleanAmPart = amPart.replace(/\s+AM$/i, '').trim();
      const cleanPmPart = pmPart.replace(/\s+PM$/i, '').trim();
      
      return {
        type: 'conditional',
        am: this.parseSimpleFormatContent(cleanAmPart) as any,
        pm: this.parseSimpleFormatContent(cleanPmPart) as any
      };
    }

    return this.parseSimpleFormatContent(trimmedContent);
  }

  private static parseSimpleFormatContent(content: string): Omit<ModelFormatItem, 'id' | 'time'> {
    const trimmedContent = content.trim();

    // Check for interaction
    if (trimmedContent.toLowerCase() === 'interaction') {
      return { type: 'interaction' };
    }

    // Check for emoji/text content (contains emoji or special characters)
    if (/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(trimmedContent)) {
      return {
        type: 'text',
        content: trimmedContent
      };
    }

    // Check for LAUNCH pattern (e.g., "Self Swap | LAUNCH 1")
    const launchMatch = trimmedContent.match(/^(.+?)\s*\|\s*LAUNCH\s+(\d+)$/i);
    if (launchMatch) {
      const promoContent = launchMatch[1].trim();
      const launchGroup = parseInt(launchMatch[2]);
      
      // Parse the promo part recursively
      const promoItem = this.parseSimpleFormatContent(promoContent);
      if (promoItem.type === 'promo') {
        return {
          ...promoItem,
          launch_group: launchGroup
        };
      }
      // If the promo part isn't a valid promo, treat as self swap with launch
      return {
        type: 'promo',
        launch_group: launchGroup
      };
    }

    // Check for self swap
    if (trimmedContent.toLowerCase() === 'self swap') {
      return { type: 'promo' }; // No group/model specified = self swap
    }

    // Check for group promo pattern (supports A, B, C, D, 1, 2, 3, Unlock, Test, Top, Sub 100)
    const groupPromoMatch = trimmedContent.match(/^group\s+(A|B|C|D|1|2|3|Unlock|Test|Top|Sub 100)\s+promo$/i);
    if (groupPromoMatch) {
      // Preserve exact case for our valid groups
      const validGroups = ['A', 'B', 'C', 'D', '1', '2', '3', 'Unlock', 'Test', 'Top', 'Sub 100'];
      const matchedGroup = validGroups.find(g => g.toLowerCase() === groupPromoMatch[1].toLowerCase()) || groupPromoMatch[1].toUpperCase();
      
      return {
        type: 'promo',
        group: matchedGroup
      };
    }

    // Check for model-specific promo pattern
    const modelPromoMatch = trimmedContent.match(/^(.+?)\s+promo$/i);
    if (modelPromoMatch) {
      const modelName = modelPromoMatch[1].trim();
      // Check if it's not a group pattern we missed
      if (!/^group\s+(A|B|C|D|1|2|3|Unlock|Test|Top|Sub 100)$/i.test(modelName)) {
        return {
          type: 'promo',
          model: modelName.toLowerCase()
        };
      }
    }

    // Check for just "promo" without specific target
    if (trimmedContent.toLowerCase() === 'promo') {
      return { type: 'promo' }; // Generic promo, could be self swap
    }

    // If none of the patterns match, treat as text content
    return {
      type: 'text',
      content: trimmedContent
    };
  }

  public static decodeFormatText(text: string): ModelFormatItem[] {
    const lines = text.split('\n').filter(line => line.trim());
    const items: ModelFormatItem[] = [];

    for (const line of lines) {
      const match = line.match(/^(\d{1,2}):(\d{2})\s+(.+)$/);
      if (!match) {
        throw new Error(`Invalid format line: ${line}`);
      }

      const [, hours, minutes, description] = match;
      const timeString = `${hours.padStart(2, '0')}:${minutes}`;

      try {
        // Validate time format
        this.parseTimeString(timeString);
        
        // Parse the content
        const formatContent = this.parseFormatItemContent(description);
        
        items.push({
          id: uuidv4(),
          time: timeString,
          ...formatContent
        });
      } catch (error) {
        throw new Error(`Error parsing line "${line}": ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return items;
  }

  // Helper method to convert new format back to display text for editing
  public static encodeFormatItems(items: ModelFormatItem[]): string {
    return items.map(item => {
      const [hours, minutes] = item.time.split(':');
      const hour = parseInt(hours);
      const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
      const timeStr = `${displayHour.toString().padStart(2, '0')}:${minutes}`;

      // Handle conditional items
      if (item.type === 'conditional' && item.am && item.pm) {
        const amContent = this.encodeSimpleFormat(item.am);
        const pmContent = this.encodeSimpleFormat(item.pm);
        return `${timeStr} ${amContent} AM / ${pmContent} PM`;
      }


      return `${timeStr} ${this.encodeSimpleFormat(item)}`;
    }).join('\n');
  }

  private static encodeSimpleFormat(item: Omit<ModelFormatItem, 'id' | 'time'> | { type: 'interaction' | 'promo' | 'text'; content?: string; group?: string; model?: string; launch_group?: number; }): string {
    switch (item.type) {
      case 'interaction':
        return 'Interaction';
      case 'text':
        return item.content || '';
      case 'promo':
        let promoText = '';
        if (item.model) {
          promoText = `${item.model} Promo`;
        } else if (item.group) {
          promoText = `Group ${item.group} Promo`;
        } else {
          promoText = 'Self Swap';
        }
        
        // Add LAUNCH suffix if launchGroup is present
        if (item.launch_group) {
          promoText += ` | LAUNCH ${item.launch_group}`;
        }
        
        return promoText;
      default:
        return '';
    }
  }
}